import pythonApiClient from './pythonBaseApi';

/**
 * Servicio para gestión de autenticación
 * Maneja login, logout y operaciones relacionadas con autenticación
 * 
 * NOTA: Los endpoints de autenticación usan Python (FastAPI)
 */
export const authApi = {
  /**
   * Inicia sesión con username y password
   * @param {string} username - Nombre de usuario
   * @param {string} password - Contraseña
   * @returns {Promise} - Respuesta de la API con token
   */
  login: async (username, password) => {
    try {
      // El API Python requiere el campo 'username'
      const loginData = {
        username: username,
        password: password
      };
      
      console.log('🔵 Enviando datos de login a Python:', loginData);
      
      // Usar Python API para login
      const response = await pythonApiClient.post('/seguridad/auth/login', loginData);
      
      // Python retorna { success, message, token, user }
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        // Guardar también el usuario si viene en la respuesta
        if (response.data.user) {
          localStorage.setItem('user', JSON.stringify(response.data.user));
        }
        
        // Retornar en formato compatible con el frontend
        return {
          ...response,
          data: {
            token: response.data.token,
            user: response.data.user
          }
        };
      }
      
      return response;
    } catch (error) {
      if (error.response?.status >= 500) {
        console.error('Error al iniciar sesión', error);
        throw new Error('Error del servidor al iniciar sesión');
      }
      throw error;
    }
  },

  /**
   * Registra un nuevo usuario
   * @param {Object} userData - Datos del usuario
   * @param {string} userData.nombre - Nombre del usuario
   * @param {string} userData.email - Email del usuario
   * @param {string} userData.password - Contraseña
   * @param {number} userData.numero_documento - Número de documento
   * @param {boolean} userData.acceso_corporativo - Acceso corporativo
   * @returns {Promise} - Respuesta de la API con token
   */
  register: async (userData) => {
    try {
      console.log('🔵 Enviando datos de registro a Python:', userData);
      
      // Usar Python API para registro
      const response = await pythonApiClient.post('/seguridad/auth/register', userData);
      
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        // Guardar también el usuario si viene en la respuesta
        if (response.data.user) {
          localStorage.setItem('user', JSON.stringify(response.data.user));
        }
      }
      
      return response;
    } catch (error) {
      if (error.response?.status >= 500) {
        console.error('Error al registrar usuario', error);
        throw new Error('Error del servidor al registrar usuario');
      }
      throw error;
    }
  },

  /**
   * Cierra la sesión del usuario
   * @returns {Promise} - Respuesta de la API
   */
  logout: async () => {
    try {
      // Usar Python API para logout
      const response = await pythonApiClient.post('/seguridad/auth/logout');
      
      // Limpiar token del localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('selectedProject');
      
      return response.data;
    } catch (error) {
      // Aunque falle la API, limpiar datos locales
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('selectedProject');
      
      if (error.response?.status >= 500) {
        console.error('Error al cerrar sesión', error);
        throw new Error('Error del servidor al cerrar sesión');
      }
      throw error;
    }
  },

  /**
   * Verifica si el usuario está autenticado
   * @returns {boolean} - True si está autenticado
   */
  isAuthenticated: () => {
    const token = localStorage.getItem('token');
    return !!token;
  },

  /**
   * Obtiene el token actual
   * @returns {string|null} - Token actual o null
   */
  getToken: () => {
    return localStorage.getItem('token');
  },

  /**
   * Obtiene el usuario actual desde localStorage
   * @returns {Object|null} - Usuario actual o null
   */
  getCurrentUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  /**
   * Obtiene el perfil del usuario autenticado desde el servidor
   * @returns {Promise} - Respuesta de la API con perfil del usuario
   */
  me: async () => {
    try {
      // Usar Python API para obtener perfil del usuario
      const response = await pythonApiClient.get('/seguridad/auth/me');
      
      // Actualizar localStorage con los datos del servidor
      if (response.data) {
        localStorage.setItem('user', JSON.stringify(response.data));
      }
      
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        // Token inválido, limpiar localStorage
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('selectedProject');
      }
      
      if (error.response?.status >= 500) {
        console.error('Error al obtener perfil del usuario', error);
        throw new Error('Error del servidor al obtener perfil del usuario');
      }
      throw error;
    }
  },

  /**
   * Renueva el token de autenticación
   * @returns {Promise} - Respuesta de la API con nuevo token
   */
  refresh: async () => {
    try {
      // Usar Python API para renovar token
      const response = await pythonApiClient.post('/seguridad/auth/refresh');
      
      // Actualizar token en localStorage
      if (response.data?.token) {
        localStorage.setItem('token', response.data.token);
      }
      
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        // Token inválido, limpiar localStorage
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('selectedProject');
      }
      
      if (error.response?.status >= 500) {
        console.error('Error al renovar token', error);
        throw new Error('Error del servidor al renovar token');
      }
      throw error;
    }
  },

  /**
   * Obtiene los proyectos asignados a un usuario específico
   * @param {number} userId - ID del usuario
   * @returns {Promise} - Respuesta de la API con proyectos del usuario
   */
  getUsuariosProyectos: async (userId) => {
    try {
      // Usar Python API para obtener proyectos del usuario
      const response = await pythonApiClient.get(`/mantenimientos/usuarios/${userId}/proyectos`);
      return response.data;
    } catch (error) {
      if (error.response?.status >= 500) {
        console.error('Error al obtener proyectos del usuario', error);
        throw new Error('Error del servidor al obtener proyectos del usuario');
      }
      throw error;
    }
  },

  /**
   * Obtiene todos los proyectos disponibles (sin autenticación)
   * @returns {Promise} - Respuesta de la API con proyectos
   */
  getAllProjects: async () => {
    try {
      // Crear una instancia temporal sin interceptors de token
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/proyectos/public`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error al obtener proyectos públicos:', error);
      throw error;
    }
  }
};

export default authApi;
